// AdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  Rating,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search,
  Pending,
  CheckCircle,
  Assignment,
  LocalOffer,
  People,
  Store,
  BarChart,
  Send,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Legend } from 'recharts';

// socket connection (uses same env var pattern)
const socket = io(process.env.REACT_APP_API_URL);

// Utility to detect emails / phone numbers (used for quick validation)
const wordsToNumbers = (text) => {
  const map = {
    zero: '0', one: '1', two: '2', three: '3', four: '4',
    five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  };
  return text.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (m) => map[m.toLowerCase()]);
};
const detectPhoneNumbers = (text) => {
  const converted = wordsToNumbers(text);
  return /(\+?\d{1,3})?[-.\s()]?\d{3,15}/g.test(converted);
};
const detectEmails = (text) => {
  const cleaned = text
    .replace(/\s+/g, '')
    .replace(/\[at\]|\(at\)/gi, '@')
    .replace(/\s*@\s*/g, '@')
    .replace(/\s*\.\s*/g, '.');
  return /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/.test(cleaned);
};

const AdminDashboard = ({ user }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const cardGradients = [
  'linear-gradient(135deg, #03BAA5 0%, #4B698E 100%)',
  'linear-gradient(135deg, #4B698E 0%, #03BAA5 100%)',
  'linear-gradient(135deg, #03BAA5 0%, #00BCD4 100%)',
  'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
  'linear-gradient(135deg, #FFA726 0%, #FB8C00 100%)',
];

  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Fetch data
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || ''; // fallback to relative
      const [eRes, dRes, uRes] = await Promise.all([
        axios.get(`${base}/api/inquiries`),
        axios.get(`${base}/api/dealers`),
        axios.get(`${base}/api/users`),
      ]);
      setEnquiries(eRes.data || []);
      setDealers(dRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      console.error('Fetch failed:', err);
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Realtime updates: refresh on new enquiry or update
  useEffect(() => {
    socket.on('newEnquiry', () => {
      fetchAll();
    });
    socket.on('updateEnquiry', () => {
      fetchAll();
    });
    return () => {
      socket.off('newEnquiry');
      socket.off('updateEnquiry');
    };
  }, [fetchAll]);

  // Stats
  const totalDealers = dealers.length;
  const totalUsers = users.length;
  const totalEnquiries = enquiries.length;
  const resolvedEnquiries = enquiries.filter(e => e.status === 'completed').length;
  const pendingEnquiries = enquiries.filter(e => e.status === 'new' || e.status === 'assigned').length;

  // Date calculations
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);

  const weeklyEnquiries = enquiries.filter(e => new Date(e.createdAt) >= oneWeekAgo);
  const monthlyEnquiries = enquiries.filter(e => new Date(e.createdAt) >= oneMonthAgo);

  const weeklyResolved = weeklyEnquiries.filter(e => e.status === 'completed').length;
  const monthlyResolved = monthlyEnquiries.filter(e => e.status === 'completed').length;

  const weeklyPending = weeklyEnquiries.filter(e => e.status !== 'completed').length;
  const monthlyPending = monthlyEnquiries.filter(e => e.status !== 'completed').length;

  // Chart data: last 7 days
  const dailyData = [...Array(7)].map((_, i) => {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const count = enquiries.filter(
      e => new Date(e.createdAt).toDateString() === date.toDateString()
    ).length;
    return { date: dateStr, enquiries: count };
  }).reverse();

  // Filtering logic by tab/search
  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesSearch =
      enquiry.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.name?.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === 0) return matchesSearch;
    if (activeTab === 1) return matchesSearch && enquiry.status === 'new';
    if (activeTab === 2) return matchesSearch && enquiry.status === 'assigned';
    if (activeTab === 3) return matchesSearch && enquiry.status === 'completed';
    return matchesSearch;
  });

  // Open assign dialog
  const openAssign = (enq) => {
    setSelectedEnquiry(enq);
    setAssignOpen(true);
  };

  // Assign dealer
  const handleAssign = async (dealer) => {
    // Validate: prevent exposing contact info in notes (example)
    if (selectedEnquiry?.notes && (detectPhoneNumbers(selectedEnquiry.notes) || detectEmails(selectedEnquiry.notes))) {
      setSnackbar({ open: true, message: 'Enquiry notes contain contact info — cannot assign directly.', severity: 'warning' });
      return;
    }

    try {
      setSnackbar({ open: true, message: 'Assigning dealer...', severity: 'info' });
      const base = process.env.REACT_APP_API_URL || '';
      await axios.patch(`${base}/api/inquiries/${selectedEnquiry._id}/assign`, {
        dealerId: dealer._id,
      });
      setSnackbar({ open: true, message: 'Dealer assigned', severity: 'success' });
      setAssignOpen(false);
      setSelectedEnquiry(null);
      fetchAll();
      // notify via socket (optional)
      socket.emit('assignedDealer', { enquiryId: selectedEnquiry._id, dealerId: dealer._id });
    } catch (err) {
      console.error('Assign failed:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Assignment failed', severity: 'error' });
    }
  };

  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  // Dialog cancel
  const handleCloseAssign = () => {
    setAssignOpen(false);
    setSelectedEnquiry(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Admin Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Welcome, {user?.name || 'Admin'}</Typography>
          <Avatar sx={{ bgcolor: '#03BAA5' }}>{user?.name?.[0] || 'A'}</Avatar>
        </Box>
      </Box>

{/* 🌈 STATS OVERVIEW - Full Width & Centered */}
<Box
  sx={{
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    mb: 4,
  }}
>
  <Grid
    container
    spacing={3}
    justifyContent="center"
    alignItems="stretch"
    sx={{
      width: '100%',
      maxWidth: '1600px', // keeps layout neat on large monitors
      px: { xs: 2, sm: 4, md: 6 },
    }}
  >
    {[
      { title: 'Dealers', value: totalDealers, icon: <Store /> },
      { title: 'Users', value: totalUsers, icon: <People /> },
      { title: 'Enquiries', value: totalEnquiries, icon: <Assignment /> },
      { title: 'Resolved', value: resolvedEnquiries, icon: <CheckCircle /> },
      { title: 'Pending', value: pendingEnquiries, icon: <Pending /> },
    ].map((item, i) => (
      <Grid
        item
        xs={12}
        sm={6}
        md={2.4}
        key={i}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'stretch',
        }}
      >
        <Card
          sx={{
            background: cardGradients[i],
            color: '#fff',
            borderRadius: 4,
            p: 3,
            width: '100%',
            maxWidth: 300,
            height: '100%',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 },
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              {item.icon} {item.title}
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {item.value}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
</Box>


      {/* Reports + Chart */}
<Paper
  sx={{
    px: { xs: 2, md: 6 },
    py: { xs: 3, md: 5 },
    mb: 6,
    borderRadius: 4,
    boxShadow: 5,
    width: '100%',
    maxWidth: '1200px',
    mx: 'auto',
    background: 'linear-gradient(180deg, #f9fbfc 0%, #ffffff 100%)',
  }}
>
  <Typography
    variant="h5"
    fontWeight={700}
    gutterBottom
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 4,
      color: '#333',
    }}
  >
    <BarChart sx={{ color: '#03BAA5' }} /> Weekly & Monthly Reports
  </Typography>

  {/* ✅ Two-column Pie Charts */}
  <Grid
    container
    spacing={4}
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, // 2 columns on md+, 1 column on small screens
      justifyContent: 'center',
      alignItems: 'center',
      gap: 4,
      mb: 6,
    }}
  >
    {/* 🟢 Weekly Report Chart */}
    <Card
      sx={{
        borderRadius: 4,
        bgcolor: '#E8F5E9',
        boxShadow: 4,
        textAlign: 'center',
        width: '80%',
        height: '100%',
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: '#2E7D32',
          fontWeight: 700,
          mb: 2,
          textAlign: 'center',
        }}
      >
        Weekly Report
      </Typography>
      <Box sx={{ width: '100%', height: { xs: 250, sm: 320, md: 350 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              data={[
                { name: 'Resolved', value: weeklyResolved, fill: '#4CAF50' },
                { name: 'Pending', value: weeklyPending, fill: '#FF9800' },
                { name: 'Total Enquiries', value: weeklyEnquiries.length, fill: '#03BAA5' },
              ]}
              outerRadius="80%"
              innerRadius="50%"
              labelLine={false}
              label={({ name, percent }) =>
                `${name} ${(percent * 100).toFixed(0)}%`
              }
              paddingAngle={3}
              isAnimationActive={true}
              animationDuration={1200}
            />
            <Tooltip />
            <Legend verticalAlign="bottom" height={36} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Card>

    {/* 🟡 Monthly Report Chart */}
    <Card
      sx={{
        borderRadius: 4,
        bgcolor: '#FFF8E1',
        boxShadow: 4,
        textAlign: 'center',
        width: '80%',
        height: '100%',
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: '#EF6C00',
          fontWeight: 700,
          mb: 2,
          textAlign: 'center',
        }}
      >
        Monthly Report
      </Typography>
      <Box sx={{ width: '100%', height: { xs: 250, sm: 320, md: 350 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              data={[
                { name: 'Resolved', value: monthlyResolved, fill: '#81C784' },
                { name: 'Pending', value: monthlyPending, fill: '#FFB74D' },
                { name: 'Total Enquiries', value: monthlyEnquiries.length, fill: '#03A9F4' },
              ]}
              outerRadius="80%"
              innerRadius="50%"
              labelLine={false}
              label={({ name, percent }) =>
                `${name} ${(percent * 100).toFixed(0)}%`
              }
              paddingAngle={3}
              isAnimationActive={true}
              animationDuration={1200}
            />
            <Tooltip />
            <Legend verticalAlign="bottom" height={36} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  </Grid>

  {/* Divider */}
  <Divider sx={{ my: 6 }} />

  {/* 📈 Daily Enquiries (7-day Trend) */}
  <Typography
    variant="h6"
    fontWeight={700}
    gutterBottom
    sx={{
      textAlign: 'center',
      color: '#4B698E',
      mb: 2,
    }}
  >
    Daily Enquiries (Last 7 Days)
  </Typography>

  <Box
    sx={{
      width: '100%',
      height: { xs: 250, sm: 320, md: 350 },
      mx: 'auto',
    }}
  >
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={dailyData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis />
        <Tooltip />
        <Line type="monotone" dataKey="enquiries" stroke="#03BAA5" strokeWidth={3} />
      </LineChart>
    </ResponsiveContainer>
  </Box>
</Paper>




      {/* Tabs and Search */}
      <Paper sx={{
        p: 2,
        mb: 3,
        borderRadius: 3,
        background: 'linear-gradient(90deg, #03BAA5 0%, #4B698E 100%)',
        color: 'white',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Tabs
            value={activeTab}
            onChange={(e,v) => setActiveTab(v)}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: 'white', height: 3 } }}
            sx={{
              '& .MuiTab-root': { color: '#fff', fontWeight: 600, textTransform: 'none' },
              '& .Mui-selected': { color: '#ffeb3b !important' },
            }}
          >
            <Tab label="All" />
            <Tab label="New" icon={<Pending />} iconPosition="start" />
            <Tab label="Assigned" icon={<Assignment />} iconPosition="start" />
            <Tab label="Completed" icon={<CheckCircle />} iconPosition="start" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search enquiries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ bgcolor: 'white', borderRadius: 2, width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 4 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#03BAA5' }}>
              <TableRow>
                {['Service', 'Customer', 'Date', 'Status', 'Dealer', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 600, textTransform: 'uppercase' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredEnquiries.map(e => (
                <TableRow hover key={e._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalOffer /> {e.service}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight={700}>{e.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{e.email}</Typography>
                  </TableCell>

                  <TableCell>{new Date(e.createdAt).toLocaleDateString()}</TableCell>

                  <TableCell>
                    <Chip label={e.status} color={
                      e.status === 'new' ? 'primary' :
                        e.status === 'assigned' ? 'secondary' : 'success'
                    } size="small" />
                  </TableCell>

                  <TableCell>{e.assignedDealer?.name || 'Not Assigned'}</TableCell>

                  <TableCell>
                    {e.status === 'new' ? (
                      <Button size="small" variant="outlined" onClick={() => openAssign(e)}>Assign</Button>
                    ) : (
                      <Button size="small" variant="contained" color="secondary" onClick={() => {
                        // quick view or navigate to detail page
                        setSelectedEnquiry(e);
                        setAssignOpen(true); // reuse dialog to show details and allow reassignment
                      }}>View</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignOpen} onClose={handleCloseAssign} fullWidth maxWidth="md">
        <DialogTitle sx={{ background: 'linear-gradient(90deg, #03BAA5, #4B698E)', color: 'white' }}>
          {selectedEnquiry ? `Assign Dealer — ${selectedEnquiry.service}` : 'Assign Dealer'}
        </DialogTitle>

        <DialogContent dividers>
          {selectedEnquiry ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{selectedEnquiry.name} — {selectedEnquiry.email}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedEnquiry.description || '—'}</Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dealers
                      .filter(d => (
                        (d.service && selectedEnquiry.service && d.service.toLowerCase() === selectedEnquiry.service.toLowerCase())
                        || (d.subService && selectedEnquiry.service && d.subService.toLowerCase() === selectedEnquiry.service.toLowerCase())
                      ))
                      .map(d => (
                        <TableRow key={d._id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar>{d.name?.[0]}</Avatar>
                              <Box>
                                <Typography>{d.name}</Typography>
                                <Typography variant="body2" color="text.secondary">{d.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{d.service}{d.subService ? ` / ${d.subService}` : ''}</TableCell>
                          <TableCell>{d.location}</TableCell>
                          <TableCell><Rating value={d.rating || 0} precision={0.5} readOnly size="small" /></TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
                                '&:hover': { background: 'linear-gradient(90deg, #4B698E, #03BAA5)' },
                              }}
                              onClick={() => handleAssign(d)}
                            >
                              Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography>No enquiry selected</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAssign} startIcon={<Send />}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
